import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  DndContext, DragOverlay, closestCenter,
  PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy,
  useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { X, Plus, MoreHorizontal, AlertTriangle, ChevronDown, ListChecks } from "lucide-react";
import TaskDetailModal, { dueDateStatus } from "./TaskDetailModal";
import RemindersSection from "@/components/RemindersSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BG_IMAGE = "https://customer-assets.emergentagent.com/job_37d91874-055e-4733-b3dd-bdd99f83cc88/artifacts/1sq98tty_freepik__create-an-background-image-without-the-login-based__59111.jpeg";
const WIP_LIMIT = 3;
const COLS = ["todo", "in_progress", "done"];
const COL_LABELS = { todo: "To Do", in_progress: "In Progress", done: "Done" };
const EMPTY = {
  todo: "No tasks yet. Click + Add Task to create one.",
  in_progress: "Drag tasks here when you start working on them.",
  done: "Completed tasks will appear here.",
};
const PRIORITY_COLORS = {
  urgent: { bg: "rgba(220,38,38,0.12)", text: "#dc2626", border: "rgba(220,38,38,0.3)" },
  high:   { bg: "rgba(217,119,6,0.12)", text: "#d97706", border: "rgba(217,119,6,0.3)" },
};

export default function KanbanBoard({ project, token, onClose, projects = [], onGamificationData }) {
  const [columns, setColumns] = useState({ todo: [], in_progress: [], done: [] });
  const [activeTask, setActiveTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [addingTodo, setAddingTodo] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [quickTitle, setQuickTitle] = useState("");
  const [overLimitCol, setOverLimitCol] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const addRef = useRef(null);
  const quickRef = useRef(null);
  const dragSourceCol = useRef(null);
  const authHeaders = { Authorization: `Bearer ${token}` };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const showToast = (msg, type = "error") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchTasks = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/projects/${project.id}/kanban`,
        { headers: authHeaders }
      );
      const grouped = { todo: [], in_progress: [], done: [] };
      data.forEach((t) => {
        const col = t.kanban_status || "todo";
        (grouped[col] || (grouped[col] = [])).push(t);
      });
      COLS.forEach((c) => grouped[c].sort((a, b) => (a.kanban_order ?? 0) - (b.kanban_order ?? 0)));
      setColumns(grouped);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [project.id]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  useEffect(() => { if (addingTodo) addRef.current?.focus(); }, [addingTodo]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const findCol = (taskId) => COLS.find((c) => columns[c].some((t) => t.id === taskId));
  const findInCols = (cols, taskId) => COLS.flatMap((c) => cols[c]).find((t) => t.id === taskId);
  const getDestCol = (over, cols) => {
    if (!over) return null;
    if (COLS.includes(over.id)) return over.id;
    return COLS.find((c) => cols[c].some((t) => t.id === over.id));
  };

  const handleDragStart = ({ active }) => {
    const task = COLS.flatMap((c) => columns[c]).find((t) => t.id === active.id);
    dragSourceCol.current = findCol(active.id);
    setActiveTask(task || null);
  };

  const handleDragOver = ({ active, over }) => {
    if (!over) return;
    const sourceCol = findCol(active.id);
    const destCol = getDestCol(over, columns);
    if (!sourceCol || !destCol || sourceCol === destCol) return;

    // WIP limit visual block
    if (destCol === "in_progress") {
      const count = columns.in_progress.filter((t) => t.id !== active.id).length;
      if (count >= WIP_LIMIT) { setOverLimitCol(true); return; }
    }
    setOverLimitCol(false);

    setColumns((prev) => {
      const task = findInCols(prev, active.id);
      if (!task) return prev;
      const newSrc = prev[sourceCol].filter((t) => t.id !== active.id);
      const overIdx = prev[destCol].findIndex((t) => t.id === over.id);
      const newDest = [...prev[destCol]];
      newDest.splice(overIdx >= 0 ? overIdx : newDest.length, 0, { ...task, kanban_status: destCol });
      return { ...prev, [sourceCol]: newSrc, [destCol]: newDest };
    });
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveTask(null);
    setOverLimitCol(false);
    if (!over) { fetchTasks(); return; }

    const currentCol = findCol(active.id);
    const srcCol = dragSourceCol.current;

    // WIP hard block
    if (currentCol === "in_progress" && srcCol !== "in_progress") {
      if (columns.in_progress.length > WIP_LIMIT) {
        showToast(`Cannot add task — In Progress limit is ${WIP_LIMIT}/3. Complete or move a task first.`);
        fetchTasks(); return;
      }
    }

    // Reorder within same column
    if (currentCol === srcCol && over.id !== active.id) {
      setColumns((prev) => {
        const col = prev[currentCol];
        const oldIdx = col.findIndex((t) => t.id === active.id);
        const newIdx = col.findIndex((t) => t.id === over.id);
        if (oldIdx === newIdx || newIdx < 0) return prev;
        return { ...prev, [currentCol]: arrayMove(col, oldIdx, newIdx) };
      });
    }

    // Persist
    const col = columns[currentCol];
    const idx = col.findIndex((t) => t.id === active.id);
    const updates = { kanban_status: currentCol, kanban_order: idx >= 0 ? idx : col.length };
    if (currentCol === "done" && srcCol !== "done") updates.completed_at = new Date().toISOString();
    if (srcCol === "done" && currentCol !== "done") updates.completed_at = null;
    if (currentCol === "in_progress" && srcCol !== "in_progress") {
      updates.status_changed_at = new Date().toISOString();
    }

    try {
      const { data: gamData } = await axios.patch(`${BACKEND_URL}/api/tasks/${active.id}/kanban`, updates, { headers: authHeaders });
      if (gamData?.xp_earned > 0 && onGamificationData) onGamificationData(gamData);
    } catch {
      showToast("Failed to save — refreshing.");
      fetchTasks();
    }
  };

  const handleAddTask = async () => {
    const title = newTitle.trim();
    if (!title) { setAddingTodo(false); return; }
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/projects/${project.id}/tasks`,
        { title, kanban_status: "todo", kanban_order: 0 },
        { headers: authHeaders }
      );
      setColumns((prev) => ({ ...prev, todo: [data, ...prev.todo] }));
      setNewTitle("");
      setAddingTodo(false);
    } catch { showToast("Failed to add task"); }
  };

  const handleQuickAdd = async () => {
    const title = quickTitle.trim();
    if (!title) return;
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/projects/${project.id}/tasks`,
        { title, kanban_status: "todo", kanban_order: 0 },
        { headers: authHeaders }
      );
      setColumns((prev) => ({ ...prev, todo: [data, ...prev.todo] }));
      setQuickTitle("");
      showToast(`"${title.length > 35 ? title.substring(0, 35) + "…" : title}" added to To Do`, "success");
    } catch { showToast("Failed to add task"); }
  };

  const handleDelete = async (taskId) => {
    const snap = { ...columns };
    setColumns((prev) => {
      const next = {};
      COLS.forEach((c) => { next[c] = prev[c].filter((t) => t.id !== taskId); });
      return next;
    });
    try {
      await axios.delete(`${BACKEND_URL}/api/tasks/${taskId}`, { headers: authHeaders });
    } catch { setColumns(snap); showToast("Failed to delete"); }
  };

  const handleEdit = async (taskId, title) => {
    setColumns((prev) => {
      const next = {};
      COLS.forEach((c) => { next[c] = prev[c].map((t) => t.id === taskId ? { ...t, title } : t); });
      return next;
    });
    try {
      await axios.patch(`${BACKEND_URL}/api/tasks/${taskId}/kanban`, { title }, { headers: authHeaders });
    } catch { fetchTasks(); }
  };

  const handlePriority = async (taskId, priority) => {
    setColumns((prev) => {
      const next = {};
      COLS.forEach((c) => { next[c] = prev[c].map((t) => t.id === taskId ? { ...t, priority } : t); });
      return next;
    });
    try {
      await axios.patch(`${BACKEND_URL}/api/tasks/${taskId}/kanban`, { priority }, { headers: authHeaders });
    } catch { fetchTasks(); }
  };

  const handleMoveToProject = async (taskId, targetProjectId) => {
    setColumns((prev) => {
      const next = {};
      COLS.forEach((c) => { next[c] = prev[c].filter((t) => t.id !== taskId); });
      return next;
    });
    try {
      await axios.patch(`${BACKEND_URL}/api/tasks/${taskId}/assign`, { project_id: targetProjectId }, { headers: authHeaders });
      const target = projects.find((p) => p.id === targetProjectId);
      showToast(`Moved to ${target ? `${target.emoji} ${target.title}` : "project"}`, "success");
    } catch {
      fetchTasks();
      showToast("Failed to move task");
    }
  };

  const totalTasks = COLS.reduce((s, c) => s + columns[c].length, 0);
  const isWork = project.category === "work";

  return (
    <div
      data-testid="kanban-board"
      style={{
        position: "fixed", inset: 0, zIndex: 60,
        backgroundImage: `url(${BG_IMAGE})`,
        backgroundSize: "cover", backgroundPosition: "center bottom",
        backgroundAttachment: "fixed",
        display: "flex", flexDirection: "column",
        animation: "fadeInDown 0.25s ease",
      }}
    >
      {/* ── Header ── */}
      <div style={{
        background: "rgba(255,255,255,0.72)", backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        borderBottom: "1px solid rgba(255,255,255,0.45)",
        boxShadow: "0 2px 16px rgba(26,77,46,0.07)",
        padding: "0.875rem 1.75rem", display: "flex", alignItems: "center",
        justifyContent: "space-between", flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <span style={{ fontSize: "1.75rem" }}>{project.emoji}</span>
          <div>
            <h2 style={{ fontFamily: "'Manrope',sans-serif", fontSize: "1.1rem", fontWeight: 700, color: "#1A4D2E", margin: 0 }}>
              {project.title}
            </h2>
            <span style={{
              display: "inline-block", fontSize: "0.62rem", fontWeight: 700, padding: "0.1rem 0.45rem",
              borderRadius: "2rem", textTransform: "uppercase", letterSpacing: "0.05em",
              background: isWork ? "rgba(61,107,31,0.12)" : "rgba(212,165,116,0.15)",
              color: isWork ? "#3d6b1f" : "#92400e",
              border: isWork ? "1px solid rgba(61,107,31,0.2)" : "1px solid rgba(212,165,116,0.35)",
            }}>{project.category}</span>
          </div>
        </div>

        <p style={{ fontFamily: "'Manrope',sans-serif", color: "#6b7280", fontSize: "0.9rem", fontWeight: 500, margin: 0 }}>
          {columns.done.length} of {totalTasks} task{totalTasks !== 1 ? "s" : ""} completed
        </p>

        <button
          data-testid="kanban-close-btn"
          onClick={onClose}
          style={{
            display: "flex", alignItems: "center", gap: "0.35rem",
            padding: "0.45rem 1rem", borderRadius: "3rem",
            border: "1.5px solid rgba(26,77,46,0.3)", background: "rgba(255,255,255,0.6)",
            color: "#1A4D2E", fontFamily: "'Manrope',sans-serif", fontSize: "0.875rem", fontWeight: 600,
            cursor: "pointer", transition: "background 0.2s, color 0.2s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#1A4D2E"; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.6)"; e.currentTarget.style.color = "#1A4D2E"; }}
        >
          <X size={14} /> Close
        </button>
      </div>

      {/* ── Reminders ── */}
      <RemindersSection projectId={project.id} token={token} />

      {/* ── Quick Add bar ── */}
      <div style={{
        background: "rgba(255,255,255,0.60)", backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(255,255,255,0.40)",
        padding: "0.6rem 1.5rem",
        display: "flex", gap: "0.5rem", alignItems: "center",
        flexShrink: 0,
      }}>
        <ListChecks size={15} color="#3d6b1f" style={{ flexShrink: 0 }} />
        <input
          ref={quickRef}
          data-testid="kanban-quick-add-input"
          value={quickTitle}
          onChange={(e) => setQuickTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleQuickAdd(); }}
          placeholder="Quick add to To Do… press Enter"
          style={{
            flex: 1, padding: "0.45rem 0.875rem",
            background: "rgba(255,255,255,0.75)",
            border: "1.5px solid rgba(26,77,46,0.18)",
            borderRadius: "0.5rem",
            color: "#0F172A", fontSize: "0.875rem",
            fontFamily: "'Manrope',sans-serif",
            outline: "none", transition: "border-color 0.2s",
          }}
          onFocus={(e) => (e.target.style.borderColor = "#3d6b1f")}
          onBlur={(e) => (e.target.style.borderColor = "rgba(26,77,46,0.18)")}
        />
        <button
          data-testid="kanban-quick-add-btn"
          onClick={handleQuickAdd}
          disabled={!quickTitle.trim()}
          style={{
            padding: "0.45rem 1rem",
            background: quickTitle.trim() ? "#3d6b1f" : "rgba(26,77,46,0.15)",
            border: "none", borderRadius: "0.5rem",
            color: "#fff", fontFamily: "'Manrope',sans-serif",
            fontSize: "0.85rem", fontWeight: 600,
            cursor: quickTitle.trim() ? "pointer" : "not-allowed",
            transition: "background 0.15s",
            display: "flex", alignItems: "center", gap: "0.35rem",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) => { if (quickTitle.trim()) e.currentTarget.style.background = "#4a7f25"; }}
          onMouseLeave={(e) => { if (quickTitle.trim()) e.currentTarget.style.background = "#3d6b1f"; }}
        >
          <Plus size={13} /> Add
        </button>
      </div>

      {/* ── Columns ── */}
      <div style={{ flex: 1, overflowX: "auto", overflowY: "hidden", padding: "1.25rem 1.5rem", display: "flex", gap: "1rem", minHeight: 0 }}>
        {loading ? (
          <p style={{ color: "#1A4D2E", fontFamily: "'Manrope',sans-serif", margin: "auto" }}>Loading board…</p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter}
            onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}
          >
            {COLS.map((colId) => (
              <KanbanColumn
                key={colId}
                colId={colId}
                tasks={columns[colId]}
                wipLimit={colId === "in_progress" ? WIP_LIMIT : null}
                overLimit={colId === "in_progress" && overLimitCol}
                addingTodo={colId === "todo" && addingTodo}
                newTitle={newTitle}
                addRef={addRef}
                onStartAdd={colId === "todo" ? () => setAddingTodo(true) : null}
                onNewTitle={colId === "todo" ? setNewTitle : null}
                onAddTask={colId === "todo" ? handleAddTask : null}
                onCancelAdd={colId === "todo" ? () => { setAddingTodo(false); setNewTitle(""); } : null}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onPriority={handlePriority}
                onMoveToProject={handleMoveToProject}
                projects={projects}
                currentProjectId={project.id}
                onOpenDetail={setDetailTask}
              />
            ))}
            <DragOverlay dropAnimation={{ duration: 200, easing: "ease" }}>
              {activeTask ? <TaskCardStatic task={activeTask} /> : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: "fixed", bottom: "2rem", left: "50%", transform: "translateX(-50%)",
          background: toast.type === "error" ? "rgba(220,38,38,0.96)" : "rgba(61,107,31,0.96)",
          color: "#fff", padding: "0.7rem 1.5rem", borderRadius: "2rem",
          fontFamily: "'Manrope',sans-serif", fontSize: "0.875rem", fontWeight: 600,
          boxShadow: "0 4px 24px rgba(0,0,0,0.35)", zIndex: 80,
          display: "flex", alignItems: "center", gap: "0.5rem",
          animation: "fadeInDown 0.2s ease",
        }}>
          {toast.type === "error" && <AlertTriangle size={15} />}
          {toast.msg}
        </div>
      )}

      {/* ── Task Detail Modal ── */}
      {detailTask && (
        <TaskDetailModal
          task={detailTask}
          token={token}
          onClose={() => setDetailTask(null)}
          onUpdated={(updated) => {
            setColumns((prev) => {
              const next = {};
              COLS.forEach((c) => { next[c] = prev[c].map((t) => t.id === updated.id ? { ...t, ...updated } : t); });
              return next;
            });
            setDetailTask(null);
          }}
          onDeleted={(id) => {
            setColumns((prev) => {
              const next = {};
              COLS.forEach((c) => { next[c] = prev[c].filter((t) => t.id !== id); });
              return next;
            });
            setDetailTask(null);
          }}
        />
      )}
    </div>
  );
}

// ── KanbanColumn ───────────────────────────────────────────────────────────────

function KanbanColumn({ colId, tasks, wipLimit, overLimit, addingTodo, newTitle, addRef, onStartAdd, onNewTitle, onAddTask, onCancelAdd, onDelete, onEdit, onPriority, onMoveToProject, projects, currentProjectId, onOpenDetail }) {
  const { setNodeRef, isOver } = useDroppable({ id: colId });
  const isAtLimit = wipLimit && tasks.length >= wipLimit;
  const showRedBorder = isOver && (overLimit || isAtLimit);
  const showGreenBorder = isOver && !overLimit && !isAtLimit;

  return (
    <div style={{ flex: "1 1 0", minWidth: "260px", maxWidth: "400px", display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Column header */}
      <div style={{
        padding: "0 2px 10px",
        borderBottom: isAtLimit ? "2px solid #ef4444" : "1px solid rgba(26,77,46,0.15)",
        marginBottom: "10px", flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ color: "#1A4D2E", fontFamily: "'Manrope',sans-serif", fontWeight: 700, fontSize: "0.875rem" }}>
              {COL_LABELS[colId]}
            </span>
            <span style={{
              borderRadius: "2rem", padding: "0.05rem 0.5rem", fontSize: "0.72rem", fontWeight: 700,
              background: isAtLimit ? "rgba(239,68,68,0.12)" : "rgba(26,77,46,0.1)",
              color: isAtLimit ? "#dc2626" : "#6b7280",
              border: isAtLimit ? "1px solid rgba(239,68,68,0.3)" : "1px solid rgba(26,77,46,0.15)",
            }}>
              {tasks.length}{wipLimit ? `/${wipLimit}` : ""}
              {isAtLimit ? " ⚠" : ""}
            </span>
          </div>
          {onStartAdd && (
            <button
              data-testid="add-task-kanban-btn"
              onClick={onStartAdd}
              style={{
                display: "flex", alignItems: "center", gap: "3px",
                background: "rgba(61,107,31,0.1)", border: "1px solid rgba(61,107,31,0.3)",
                color: "#3d6b1f", borderRadius: "0.375rem", padding: "3px 8px",
                fontSize: "0.75rem", fontWeight: 600, fontFamily: "'Manrope',sans-serif",
                cursor: "pointer", transition: "background 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(61,107,31,0.2)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(61,107,31,0.1)")}
            >
              <Plus size={12} /> Add Task
            </button>
          )}
        </div>
        {isAtLimit && (
          <p style={{ color: "#dc2626", fontSize: "0.7rem", fontFamily: "'Manrope',sans-serif", margin: "4px 0 0", fontWeight: 500 }}>
            AT LIMIT — complete or move a task to continue
          </p>
        )}
      </div>

      {/* Droppable area */}
      <div
        ref={setNodeRef}
        data-testid={`kanban-col-${colId}`}
        style={{
          flex: 1, overflowY: "auto", borderRadius: "0.75rem",
          background: "rgba(255,255,255,0.18)",
          backdropFilter: "blur(3px)",
          WebkitBackdropFilter: "blur(3px)",
          border: showRedBorder ? "2px solid #ef4444" : showGreenBorder ? "2px solid #3d6b1f" : "1px solid rgba(255,255,255,0.35)",
          padding: "10px",
          transition: "border-color 0.15s",
          minHeight: "200px",
          boxShadow: "0 4px 24px rgba(26,77,46,0.06)",
        }}
      >
        {/* Inline add input */}
        {addingTodo && (
          <div style={{ marginBottom: "8px" }}>
            <input
              ref={addRef}
              data-testid="kanban-add-input"
              value={newTitle}
              onChange={(e) => onNewTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onAddTask();
                if (e.key === "Escape") onCancelAdd();
              }}
              placeholder="Task title…"
              style={{
                width: "100%", padding: "8px 10px", borderRadius: "0.5rem",
                background: "rgba(255,255,255,0.55)", border: "1.5px solid #3d6b1f",
                color: "#0F172A", fontSize: "0.85rem", fontFamily: "'Manrope',sans-serif",
                outline: "none", boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
              <button onClick={onAddTask} style={smallBtn("#3d6b1f", "#fff")}>Add</button>
              <button onClick={onCancelAdd} style={smallBtn("rgba(26,77,46,0.08)", "#374151")}>Cancel</button>
            </div>
          </div>
        )}

        <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {tasks.map((task) => (
            <TaskCard key={task.id} task={task} colId={colId} onDelete={onDelete} onEdit={onEdit} onPriority={onPriority} onMoveToProject={onMoveToProject} projects={projects} currentProjectId={currentProjectId} onOpenDetail={onOpenDetail} />
          ))}
        </SortableContext>

        {tasks.length === 0 && !addingTodo && (
          <p style={{
            color: "#9ca3af", fontSize: "0.8rem", fontFamily: "'Manrope',sans-serif",
            textAlign: "center", padding: "1.5rem 0.5rem", margin: 0,
          }}>
            {EMPTY[colId]}
          </p>
        )}
      </div>
    </div>
  );
}

// ── TaskCard (sortable) ────────────────────────────────────────────────────────

function TaskCard({ task, colId, onDelete, onEdit, onPriority, onMoveToProject, projects = [], currentProjectId, onOpenDetail }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const [showMenu, setShowMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(task.title);
  const [showPriority, setShowPriority] = useState(false);
  const [showMoveProject, setShowMoveProject] = useState(false);
  const menuRef = useRef(null);

  const otherProjects = projects.filter((p) => p.id !== currentProjectId);

  // Stale calculation: in_progress tasks not touched in 3+ days
  const staleDays = colId === "in_progress" && task.status_changed_at
    ? Math.floor((Date.now() - new Date(task.status_changed_at)) / 86400000)
    : -1;
  const isStale = staleDays >= 3;

  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) { setShowMenu(false); setShowPriority(false); setShowMoveProject(false); } };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const saveEdit = () => {
    const v = editVal.trim();
    if (v && v !== task.title) onEdit(task.id, v);
    setEditing(false);
  };

  const pc = PRIORITY_COLORS[task.priority];
  const isDone = colId === "done";

  return (
    <div
      ref={setNodeRef}
      data-testid={`task-card-${task.id}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        rotate: isDragging ? "2deg" : "0deg",
        marginBottom: "8px",
        background: "rgba(255,255,255,0.88)",
        border: isStale ? "2px solid #f59e0b" : "1px solid rgba(255,255,255,0.55)",
        borderRadius: "0.625rem",
        padding: "10px 12px",
        cursor: isDragging ? "grabbing" : "grab",
        position: "relative",
        userSelect: "none",
        boxShadow: isStale
          ? "0 2px 12px rgba(245,158,11,0.22), inset 0 0 0 1px rgba(245,158,11,0.1)"
          : "0 2px 8px rgba(26,77,46,0.06)",
      }}
      {...attributes}
      {...listeners}
    >
      {/* Stale badge */}
      {isStale && (
        <div style={{
          display: "inline-flex", alignItems: "center", gap: "0.25rem",
          background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)",
          color: "#d97706", borderRadius: "0.3rem",
          padding: "0.1rem 0.4rem", fontSize: "0.62rem", fontWeight: 700,
          fontFamily: "'Manrope',sans-serif", textTransform: "uppercase",
          letterSpacing: "0.04em", marginBottom: "5px",
        }}>
          ⚠ STALE · {staleDays}d
        </div>
      )}
      {/* Priority badge */}
      {pc && (
        <span style={{
          position: "absolute", top: "8px", right: showMenu ? "30px" : "8px",
          fontSize: "0.6rem", fontWeight: 700, padding: "1px 6px", borderRadius: "2rem",
          textTransform: "uppercase", letterSpacing: "0.05em",
          background: pc.bg, color: pc.text, border: `1px solid ${pc.border}`,
          transition: "right 0.15s",
        }}>
          {task.priority}
        </span>
      )}

      {/* Action menu button */}
      <button
        data-testid={`task-menu-${task.id}`}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); setShowMenu((v) => !v); setShowPriority(false); }}
        style={{
          position: "absolute", top: "6px", right: "6px",
          background: "none", border: "none", cursor: "pointer",
          color: showMenu ? "#374151" : "rgba(107,114,128,0.6)",
          display: "flex", padding: "2px", borderRadius: "0.25rem",
          transition: "color 0.15s",
        }}
      >
        <MoreHorizontal size={14} />
      </button>

      {/* Dropdown menu */}
      {showMenu && (
        <div
          ref={menuRef}
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            position: "absolute", top: "28px", right: "6px", zIndex: 10,
            background: "rgba(255,255,255,0.97)", border: "1px solid rgba(26,77,46,0.15)",
            borderRadius: "0.5rem", overflow: "hidden", minWidth: "140px",
            boxShadow: "0 4px 16px rgba(26,77,46,0.12)",
          }}
        >
          <MenuBtn onClick={() => { setEditing(true); setShowMenu(false); }}>Edit title</MenuBtn>
          <MenuBtn onClick={() => { setShowPriority((v) => !v); setShowMoveProject(false); }}>
            Priority <ChevronDown size={11} />
          </MenuBtn>
          {showPriority && (
            <div style={{ background: "rgba(248,250,252,0.98)", borderTop: "1px solid rgba(26,77,46,0.1)" }}>
              {["none", "low", "medium", "high", "urgent"].map((p) => (
                <MenuBtn key={p} onClick={() => { onPriority(task.id, p === "none" ? "medium" : p); setShowMenu(false); setShowPriority(false); }}>
                  <span style={{ textTransform: "capitalize" }}>{p}</span>
                </MenuBtn>
              ))}
            </div>
          )}
          <MenuBtn onClick={() => { setShowMoveProject((v) => !v); setShowPriority(false); }}>
            Move to project <ChevronDown size={11} />
          </MenuBtn>
          {showMoveProject && (
            <div style={{ background: "rgba(248,250,252,0.98)", borderTop: "1px solid rgba(26,77,46,0.1)" }}>
              {otherProjects.length === 0 ? (
                <div style={{ padding: "7px 12px", color: "#6b7280", fontSize: "0.75rem", fontFamily: "'Manrope',sans-serif" }}>
                  No other projects
                </div>
              ) : otherProjects.map((p) => (
                <MenuBtn key={p.id} onClick={() => { onMoveToProject(task.id, p.id); setShowMenu(false); setShowMoveProject(false); }}>
                  {p.emoji} {p.title}
                </MenuBtn>
              ))}
            </div>
          )}
          <div style={{ height: "1px", background: "rgba(26,77,46,0.1)" }} />
          <MenuBtn
            onClick={() => { if (window.confirm("Delete this task?")) { onDelete(task.id); setShowMenu(false); } }}
            danger
          >Delete task</MenuBtn>
        </div>
      )}

      {/* Title */}
      {editing ? (
        <input
          autoFocus
          value={editVal}
          onPointerDown={(e) => e.stopPropagation()}
          onChange={(e) => setEditVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") { setEditVal(task.title); setEditing(false); } }}
          onBlur={saveEdit}
          style={{
            width: "100%", background: "rgba(255,255,255,0.7)", border: "1.5px solid #3d6b1f",
            color: "#0F172A", borderRadius: "0.375rem", padding: "4px 6px",
            fontSize: "0.85rem", fontFamily: "'Manrope',sans-serif", outline: "none",
            boxSizing: "border-box",
          }}
        />
      ) : (
        <p
          data-testid={`kanban-task-title-${task.id}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onOpenDetail?.(task)}
          style={{
            color: isDone ? "#9ca3af" : "#1A4D2E",
            fontSize: "0.85rem", fontWeight: 500, fontFamily: "'Manrope',sans-serif",
            margin: "0 0 4px", paddingRight: pc ? "52px" : "20px",
            textDecoration: isDone ? "line-through" : "none",
            wordBreak: "break-word",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => { if (!isDone) e.currentTarget.style.color = "#3d6b1f"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = isDone ? "#9ca3af" : "#1A4D2E"; }}
        >
          {task.title}
        </p>
      )}

      {/* Description preview */}
      {!editing && task.description && (
        <p style={{
          color: "#6b7280", fontSize: "0.72rem", fontFamily: "'Manrope',sans-serif",
          margin: "0 0 4px", lineHeight: 1.4,
          overflow: "hidden", display: "-webkit-box",
          WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
        }}>
          {task.description}
        </p>
      )}

      {/* Timestamp + due date + checklist progress */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
        <p style={{ color: "#9ca3af", fontSize: "0.7rem", fontFamily: "'Manrope',sans-serif", margin: 0 }}>
          {timeAgo(task.created_at)}
        </p>
        {(() => {
          const due = dueDateStatus(task.due_date);
          if (!due) return null;
          return (
            <span style={{
              fontSize: "0.65rem", fontWeight: 600, fontFamily: "'Manrope',sans-serif",
              background: due.bg, color: due.color, border: `1px solid ${due.border}`,
              borderRadius: "0.3rem", padding: "0.05rem 0.35rem",
            }}>{due.label}</span>
          );
        })()}
        {task.checklist && task.checklist.length > 0 && (() => {
          const done = task.checklist.filter((i) => i.done).length;
          const total = task.checklist.length;
          const all = done === total;
          return (
            <span style={{
              fontSize: "0.65rem", fontWeight: 600, fontFamily: "'Manrope',sans-serif",
              color: all ? "#3d6b1f" : "#6b7280",
              background: all ? "rgba(61,107,31,0.1)" : "rgba(107,114,128,0.08)",
              border: `1px solid ${all ? "rgba(61,107,31,0.25)" : "rgba(107,114,128,0.15)"}`,
              borderRadius: "0.3rem", padding: "0.05rem 0.35rem",
            }}>✓ {done}/{total}</span>
          );
        })()}
      </div>
    </div>
  );
}

// ── TaskCardStatic (DragOverlay) ───────────────────────────────────────────────

function TaskCardStatic({ task }) {
  const pc = PRIORITY_COLORS[task.priority];
  return (
    <div style={{
      background: "rgba(255,255,255,0.92)", border: "1px solid rgba(61,107,31,0.35)",
      borderRadius: "0.625rem", padding: "10px 12px",
      boxShadow: "0 8px 32px rgba(26,77,46,0.15)",
      opacity: 0.9, rotate: "2deg",
    }}>
      {pc && (
        <span style={{
          display: "inline-block", marginBottom: "4px", fontSize: "0.6rem", fontWeight: 700,
          padding: "1px 6px", borderRadius: "2rem", textTransform: "uppercase",
          background: pc.bg, color: pc.text, border: `1px solid ${pc.border}`,
        }}>{task.priority}</span>
      )}
      <p style={{ color: "#1A4D2E", fontSize: "0.85rem", fontWeight: 500, fontFamily: "'Manrope',sans-serif", margin: 0 }}>
        {task.title}
      </p>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function MenuBtn({ onClick, danger, children }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", alignItems: "center", gap: "6px",
        width: "100%", padding: "7px 12px", background: hover ? "rgba(26,77,46,0.06)" : "transparent",
        border: "none", cursor: "pointer", textAlign: "left",
        color: danger ? "#dc2626" : "#374151",
        fontSize: "0.8rem", fontFamily: "'Manrope',sans-serif",
        transition: "background 0.12s",
      }}
    >{children}</button>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (d < 1) return "just now";
  if (d < 60) return `${d}m ago`;
  const h = Math.floor(d / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const smallBtn = (bg, color) => ({
  padding: "4px 10px", borderRadius: "0.375rem", border: "none", background: bg,
  color, fontSize: "0.75rem", fontWeight: 600, fontFamily: "'Manrope',sans-serif", cursor: "pointer",
});
